import { 
    BarChartOutlined, 
    CampaignOutlined, 
    CandlestickChartOutlined, 
    InboxRounded, 
    Inventory2Outlined, 
    LocalHospitalRounded, 
    Logout, 
    PeopleOutlined, 
    Public, 
    SupportRounded, 
    TimelineRounded, 
    WysiwygRounded 
} from '@mui/icons-material';
import { Chip, Divider, List, ListItemButton, ListItemIcon, ListItemText, Skeleton, Stack, Toolbar } from '@mui/material';
import { Box } from '@mui/system';
import React, { useContext, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router';
import logo from 'url:../../../assets/docsi_logo.svg';
import { useAuth } from '../Hooks/useAuth';
import { UserContext } from '../Hooks/useUser';
import HospitalDropdown from '../HospitalDropdown';
import ConversationCount from './ConversationCount';
import { theme } from '../../theme.js'

function SidebarContents(props) {
    const navigate = useNavigate();
    const { user, setUser, hasUserManagement, hasCardManagement, hasCampaignMonitor, hasInventoryManagement, hasImplementerInboxAccess, hasPackMonitoring, hasProcedureAnalytics, isSuperUser, hasProcedureMonitorRole } = useContext(UserContext);
    const { logout } = useAuth();
    const location = useLocation();

    function handleLogout(event) {
        event.preventDefault();
        logout().then(() => {
            setUser(null);
            navigate('/login', {replace : true})
        });
    }

    const selectedIndex = useMemo(() => {
        if (location.pathname.split('/').includes('cards')) {
            return 'cards';
        } else if (location.pathname.split('/').includes('users')) {
            return 'users';
        } else if (location.pathname.split('/').includes('inventory')) {
            if (location.pathname.split('/').includes('insights')) {
                return 'inventory.insights';
            }
            return 'inventory';
        } else if (location.pathname.split('/').includes('itemAnalytics')) {
            return 'inventory.insights';
        } else if (location.pathname.split('/').includes('hospitals')) {
            return 'hospitals';
        } else if (location.pathname.split('/').includes('physicians')) {
            return 'physicians';
        } else if (location.pathname.split('/').includes('physicianTexting')) {
            return 'physicianTexting';
        } else if (location.pathname.split('/').includes('packs')) {
            return 'campaignMonitoring.packs';
        } else if (location.pathname.split('/').includes('analytics')) { 
            return 'procedureAnalytics';
        } else if (location.pathname.split('/').includes('procedureInsights')) {
            return 'procedureInsights';
        } else if (location.pathname.split('/').includes('procedures')) {
            return 'procedureInsights';
        } else if (location.pathname.split('/').includes('campaignMonitoring')) {
            if (location.pathname.split('/').includes('cardManagers')) {
                return 'campaignMonitoring.cardManagers';
            } else if (location.pathname.split('/').includes('physicians')) {
                return 'campaignMonitoring.physicians';
            }
            return 'campaignMonitoring.campaigns';
        } else if (location.pathname.split('/').includes('campaigns')) {
            return 'campaigns';
        } else if (location.pathname.split('/').includes('implementerInbox')) {
            return 'implementerInbox';
        }
    }, [location]);

    return (
        <>
            <Toolbar sx={{ padding: '16px', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                { user.hospital ? <img src={user.hospital.logo ? user.hospital.logo : logo} alt='Site Logo' style={{ objectFit: 'contain' }} width={200}/> : <Skeleton variant='rectangle' width={150} height={20} /> }
            </Toolbar>
            <Divider />
            <Box 
                sx={{ 
                    flex: 1, 
                    overflowY: 'auto',
                    paddingBottom: '60px',
                    maxHeight: 'calc(100vh - 120px)' // Account for toolbar height
                }}
            >
                <List>
                    { hasCardManagement() &&
                        <ListItemButton key={0} selected={selectedIndex === 'cards'} onClick={() => navigate('/cards')}>
                            <ListItemIcon>
                                <WysiwygRounded />
                            </ListItemIcon>
                            <ListItemText primary='Preference Cards' />
                        </ListItemButton>
                    }
                    { hasUserManagement() && 
                        <ListItemButton key={1} selected={selectedIndex === 'users'} onClick={() => navigate('/users')}>
                            <ListItemIcon>
                                <PeopleOutlined />
                            </ListItemIcon>
                            <ListItemText primary='Users' />
                        </ListItemButton>
                    }
                    { hasInventoryManagement() &&
                        <>
                            <ListItemButton key={2} selected={selectedIndex === 'inventory'} onClick={() => navigate('/inventory')}>
                                <ListItemIcon>
                                    <Inventory2Outlined />
                                </ListItemIcon>
                                <ListItemText primary='Inventory' />
                            </ListItemButton>
                            <List sx={{ py: 0 }}>
                                <ListItemButton key={14} sx={{ pl: 4, py: 0 }} selected={selectedIndex === 'inventory.insights'} onClick={() => navigate('/inventory/insights')}>
                                    <ListItemIcon>
                                        <BarChartOutlined />
                                    </ListItemIcon>
                                    <ListItemText primary={
                                        <Stack direction="row" alignItems="center" spacing={1}>
                                            Inventory Insights
                                            <BetaChip />
                                        </Stack>
                                    } />
                                </ListItemButton>
                            </List>
                        </>
                    }
                    { isSuperUser() && 
                        <ListItemButton key={3} selected={selectedIndex === 'physicians'} onClick={() => navigate('/superSettings/organizations/' + user.hospital.organization_id + '/physicians')}>
                            <ListItemIcon>
                                <LocalHospitalRounded />
                            </ListItemIcon>
                            <ListItemText primary='Physicians' />
                        </ListItemButton>
                    }
                    { hasProcedureAnalytics() &&
                        <ListItemButton key={13} selected={selectedIndex === 'procedureAnalytics'} onClick={() => navigate('/analytics')}>
                            <ListItemIcon>
                                <TimelineRounded />
                            </ListItemIcon>
                            <ListItemText 
                                primary={
                                    <Stack direction="row" alignItems="center" spacing={1}>
                                        Procedure Analytics
                                        <BetaChip />
                                    </Stack>
                                }
                            />
                        </ListItemButton>
                    }
                    { hasInventoryManagement() &&
                        <ListItemButton key={12} selected={selectedIndex === 'procedureInsights'} onClick={() => navigate('/procedureInsights')}>
                            <ListItemIcon>
                                <CandlestickChartOutlined />
                            </ListItemIcon>
                            <ListItemText primary={
                                <Stack direction="row" alignItems="center" spacing={1}>
                                    Procedure Insights
                                    <BetaChip />
                                </Stack>
                            } />
                        </ListItemButton>
                    }
                    { hasCampaignMonitor() && 
                        <>
                            { /* This line uses the user's hospital's organization ID instead of the user's org ID to allow for super users who can change hospitals and orgs to load the proper page. */ }
                            <ListItemButton key={4} onClick={() => navigate('/campaignMonitoring/campaigns')}>
                                <ListItemIcon>
                                    <BarChartOutlined />
                                </ListItemIcon>
                                <ListItemText primary='Campaign Monitoring' />
                            </ListItemButton>
                            <List sx={{ py: 0 }}>
                                <ListItemButton key={5} sx={{ pl: 4, py: 0 }} selected={selectedIndex === 'campaignMonitoring.campaigns'} onClick={() => navigate('/campaignMonitoring/campaigns')}>
                                    <ListItemIcon />
                                    <ListItemText primary='Campaigns' />
                                </ListItemButton>
                                { hasPackMonitoring() &&
                                    <ListItemButton key={6} sx={{ pl: 4, py: 0 }} selected={selectedIndex === 'campaignMonitoring.packs'} onClick={() => navigate('/campaignMonitoring/packs')}>
                                        <ListItemIcon />
                                        <ListItemText primary='Packs' />
                                    </ListItemButton>
                                }
                                <ListItemButton key={7} sx={{ pl: 4, py: 0 }} selected={selectedIndex === 'campaignMonitoring.physicians'} disabled onClick={() => navigate('/campaignMonitoring/cardManagers')}>
                                    <ListItemIcon />
                                    <ListItemText primary='Card Managers' />
                                </ListItemButton>
                                <ListItemButton key={8} sx={{ pl: 4, py: 0 }} selected={selectedIndex === 'campaignMonitoring.physicians'} disabled onClick={() => navigate('/campaignMonitoring/physicians')}>
                                    <ListItemIcon />
                                    <ListItemText primary='Physicians' />
                                </ListItemButton>
                            </List>
                        </>
                    }
                    { hasImplementerInboxAccess() &&
                        <ListItemButton key={9} selected={selectedIndex === 'implementerInbox'} onClick={() => navigate('/implementerInbox')}>
                            <ListItemIcon>
                                <InboxRounded />
                            </ListItemIcon>
                            <ListItemText primary='Implementer Inbox' />
                        </ListItemButton>
                    }
                    {/* Change this to check for a campaign management role in the furture */}
                    { isSuperUser() &&
                        <ListItemButton key={10} selected={selectedIndex === 'campaigns'} onClick={() => navigate('/campaigns')}>
                            <ListItemIcon>
                                <CampaignOutlined />
                            </ListItemIcon>
                            <ListItemText primary='Campaigns' />
                        </ListItemButton>
                    }
                </List>
                <Divider />
                <List>
                    <ListItemButton target="_blank" href = "https://docs.google.com/forms/d/e/1FAIpQLSdZk2yXi-G7yU_dhUurqcd_id2ffgXYXh46m0tNwW2EJJmD-Q/viewform" key={4} selected={selectedIndex === 'help'}>
                        <ListItemIcon>
                            <SupportRounded />
                        </ListItemIcon>
                        <ListItemText primary='Need Help?' />
                    </ListItemButton>
                    { isSuperUser() && 
                        <>
                            <ListItemButton key={11} selected={selectedIndex === 'hospitals'} onClick = {() => navigate(`/superSettings/hospitals/${user.hospital.id}`)}>
                                <ListItemIcon>
                                    <Public />
                                </ListItemIcon>
                                <ListItemText primary='Super Settings'/>
                            </ListItemButton>
                            <ConversationCount selectedIndex={selectedIndex} isSuperUser={isSuperUser()} navigate={() => navigate('/physicianTexting')} />
                        </>
                    }
                    <ListItemButton key={12} selected={selectedIndex === 'logout'} onClick={handleLogout}>
                        <ListItemIcon>
                            <Logout />
                        </ListItemIcon>
                        <ListItemText primary='Logout' />
                    </ListItemButton>
                </List>
            </Box>
            { (isSuperUser() || hasProcedureMonitorRole() || hasInventoryManagement()) && 
                <Box sx={{ width: props.drawerWidth, position: 'fixed', bottom: 0, backgroundColor: theme.palette.background.white, borderTop: '1px solid rgba(0,0,0,0.12)' }}>
                    <HospitalDropdown />
                </Box>
            }
        </>
    );
}

function BetaChip() {
    return (
        <Chip 
            label="Beta" 
            size="small" 
            sx={{ 
                fontSize: '10px', 
                height: '16px',
                backgroundColor: '#e3f2fd',
                color: '#1976d2'
            }} 
        />
    );
}

export default SidebarContents;